import {
  type Character,
  canForfeitTurn,
  type HorrorRaceState,
  isFewPlayerVariant,
  type Placement,
  selectExactSummary,
  summarizeAll,
} from "@cloudnest/horror-race-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { buildLobbyPath, navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import { ActionPanel } from "./components/ActionPanel";
import { CoinMemorySheet } from "./components/CoinMemorySheet";
import { DrawnCoinsPreview } from "./components/DrawnCoinsPreview";
import { GameOverScreen } from "./components/GameOverScreen";
import { HorrorTopFrame } from "./components/HorrorTopFrame";
import { LoadingScreen } from "./components/LoadingScreen";
import { RaceRecapView } from "./components/RaceRecapView";
import { RaceTrack } from "./components/RaceTrack";
import { RoundRecapView } from "./components/RoundRecapView";
import { SecretSelection } from "./components/SecretSelection";
import { SecretStrip } from "./components/SecretStrip";
import { ZoneBoard } from "./components/ZoneBoard";
import {
  getHostAction,
  multiplayerGameActions,
  submitHostAction,
  useMultiplayerGame,
  useMultiplayerGameActions,
} from "./multiplayer-game-store";
import { resolveHorrorRaceRouteMode } from "./route-mode";
import { usePlacementDraft } from "./use-placement-draft";
import { getHorrorPlayerToken } from "./utils/horror-tokens";

const EMPTY_COINS: readonly Character[] = [];

function ObserverModeBanner() {
  return (
    <div
      data-testid="observer-mode-banner"
      className="px-4 py-2 text-center font-semibold text-sm"
      style={{
        backgroundColor:
          "color-mix(in srgb, var(--cn-horror-p3) 14%, transparent)",
        borderBottom: "1px solid var(--cn-horror-p3)",
        color: "var(--cn-horror-p3)",
      }}
    >
      관전 모드 · 읽기 전용
    </div>
  );
}

function ObserverStatusDock({
  phase,
  currentPlayerName,
  drawnCoins,
}: {
  readonly phase: HorrorRaceState["phase"];
  readonly currentPlayerName: string;
  readonly drawnCoins: readonly Character[];
}) {
  const isResolving = phase === "ROUND_RESOLVE";
  const status = isResolving
    ? "구역 정산 중..."
    : drawnCoins.length > 0
      ? `${currentPlayerName}님이 배치 중...`
      : `${currentPlayerName}님의 차례를 기다리는 중...`;

  return (
    <div
      data-testid="horror-observer-status-dock"
      className="shrink-0 px-4 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
      style={{
        borderTop: "1px solid var(--cn-horror-surface-border)",
        backgroundColor: "var(--cn-horror-surface-secondary)",
      }}
    >
      <div className="mx-auto flex max-w-[720px] flex-col gap-2">
        <span
          data-testid="horror-observer-status"
          className="text-center font-semibold text-sm"
          style={{ color: "var(--cn-horror-text-muted)" }}
        >
          {status}
        </span>

        {drawnCoins.length > 0 && !isResolving && (
          <div className="overflow-hidden px-3 pt-2.5 pb-2.5">
            <DrawnCoinsPreview
              coins={drawnCoins}
              testId="horror-observer-drawn-coins-preview"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { gameState, myParticipantId, lastError, drawRequestSig } =
    useMultiplayerGame();
  const actions = useMultiplayerGameActions();

  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const isObserver =
    user?.id !== undefined &&
    (currentRoom?.observerUserIds ?? []).includes(user.id) &&
    !(currentRoom?.participantUserIds ?? []).includes(user.id);

  // Signature of the host action currently in flight. Keyed by the game point
  // it acts on so the guard blocks only a re-submit of the SAME action — a
  // different follow-up action (e.g. the round-2 draw after resolve_round) is
  // never blocked by the previous action's still-pending promise.
  const inFlightHostActionRef = useRef<string | null>(null);

  // M1 — coin-memory sheet. Open/close state lives here so the trigger in the
  // top frame and the overlay portal share one source of truth. Selectors run
  // on every gameState change but memoize on its reference; engine helpers are
  // pure functions of state.
  const [memoryOpen, setMemoryOpen] = useState(false);
  // "내 선택" reveal toggle — lifted here (not inside SecretStrip) so it survives
  // the phase-branch unmount at round/set settlement. An "on" strip stays on
  // across rounds and races instead of resetting to off each new round.
  const [secretOpen, setSecretOpen] = useState(false);
  const toggleSecret = useMemo(() => () => setSecretOpen((v) => !v), []);
  const memorySummaries = useMemo(
    () => (gameState ? summarizeAll(gameState) : []),
    [gameState],
  );
  const memoryExactSummary = useMemo(
    () => (gameState ? selectExactSummary(gameState) : null),
    [gameState],
  );
  const memoryEnabled =
    memorySummaries.length > 0 || memoryExactSummary !== null;
  const openMemory = useMemo(() => () => setMemoryOpen(true), []);
  const closeMemory = useMemo(() => () => setMemoryOpen(false), []);

  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [actions, lobbyActions]);

  // Real-time game events via WebSocket
  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: (eventType, payload, seq) => {
      actionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void multiplayerGameActions.replayEvents(roomId);
    },
  });

  // Initialize game state on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: using primitive deps to avoid re-firing on every WS room update
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;

    if (isObserver) {
      void multiplayerGameActions.initializeAsObserver(roomId);
    } else if (isHost) {
      const participants = currentRoom.participantUserIds ?? [];
      const playerIds =
        participants.length > 0
          ? participants
          : currentRoom.guestUserId
            ? [currentRoom.hostUserId, currentRoom.guestUserId]
            : [];
      if (playerIds.length === 0) return;
      // Resolve each player's real lobby nickname from the room participants,
      // aligned to playerIds. Falls back to "Player N" when a name is missing.
      const playerNames = playerIds.map((id: string, i: number) =>
        getParticipantDisplayName(currentRoom, id, `Player ${i + 1}`),
      );
      const totalRaces = playerIds.length <= 6 ? 3 : 2;
      void multiplayerGameActions.initializeAsHost(
        roomId,
        playerIds,
        playerNames,
        totalRaces,
      );
    } else {
      multiplayerGameActions.initializeAsGuest(roomId);
      void multiplayerGameActions.replayEvents(roomId);
    }
  }, [roomId, currentRoom?.id, user?.id, isHost, isObserver]);

  // Host orchestration: auto-submit phase transitions. Consecutive actions at a
  // round boundary (resolve_round → shuffle_and_draw) must BOTH fire even if the
  // first action's promise resolves only after its WS echo has already advanced
  // the state — otherwise the next round's first player never gets coins drawn.
  useEffect(() => {
    if (!isHost || !gameState || !roomId) return;
    if (
      (window as { __HR_DISABLE_HOST_AUTOMATION__?: boolean })
        .__HR_DISABLE_HOST_AUTOMATION__
    ) {
      return;
    }

    const action = getHostAction(gameState, drawRequestSig);
    if (!action) return;

    const sig = `${action.type}:${gameState.raceNumber}:${gameState.round}:${gameState.drawPass ?? 1}:${gameState.currentTurnIndex}`;
    if (inFlightHostActionRef.current === sig) return;

    inFlightHostActionRef.current = sig;
    let submitted = false;
    const timer = setTimeout(() => {
      submitted = true;
      void submitHostAction(roomId, action).finally(() => {
        if (inFlightHostActionRef.current === sig) {
          inFlightHostActionRef.current = null;
        }
      });
    }, 300);

    return () => {
      clearTimeout(timer);
      // Released only if we never submitted, so a re-run can retry; once
      // submitted, the promise's finally owns the release.
      if (!submitted && inFlightHostActionRef.current === sig) {
        inFlightHostActionRef.current = null;
      }
    };
  }, [isHost, gameState, roomId, drawRequestSig]);

  // Reliability net for missed WS events. The live path delivers events via a
  // MongoDB change-stream → `game:event` broadcast; a single dropped broadcast
  // otherwise strands a client (it never learns the turn advanced) with no
  // recovery but a full WS reconnect — which froze a real 3-player game mid-round.
  // Poll for events past lastAppliedSeq and apply any we missed. catchUpEvents
  // dedups by sequence, so this is a no-op while the WS is keeping up.
  useEffect(() => {
    if (!roomId) return;
    const id = setInterval(() => {
      void multiplayerGameActions.catchUpEvents(roomId);
    }, 4000);
    return () => clearInterval(id);
  }, [roomId]);

  // T23-b — the active player's placement draft, lifted here so the bottom chip
  // tray (CoinHand) and the top ZoneBoard (preview + drop target) share one
  // state. Called unconditionally (hooks rules); resets when the turn point
  // changes (new draw) — keyed by the same sig as drawTurnSig below.
  const placementDraft = usePlacementDraft(
    gameState?.drawnCoins ?? EMPTY_COINS,
    gameState
      ? `${gameState.raceNumber}:${gameState.round}:${gameState.drawPass ?? 1}:${gameState.currentTurnIndex}`
      : "",
  );

  const handleLeaveGame = () => {
    actions.reset();
    void lobbyActions.leaveGame();
    navigateTo(buildLobbyPath("horror-race", resolveHorrorRaceRouteMode()));
  };

  if (!gameState) return <LoadingScreen />;

  const myPlayer = gameState.players.find((p) => p.id === myParticipantId);
  const currentPlayerIndex = gameState.turnOrder[gameState.currentTurnIndex];
  const currentPlayer = gameState.players[currentPlayerIndex];
  const isMyTurn = myPlayer
    ? gameState.players.indexOf(myPlayer) === currentPlayerIndex
    : false;
  const isForfeitLegal =
    myParticipantId !== null && canForfeitTurn(gameState, myParticipantId);

  // T9: current turn point — must match the store's turnPointSig format. Used
  // to show the "coins are being drawn" pending state after 뽑기 but before the
  // host's draw lands.
  const drawTurnSig = `${gameState.raceNumber}:${gameState.round}:${gameState.drawPass ?? 1}:${gameState.currentTurnIndex}`;

  const playerNames: Record<string, string> = Object.fromEntries(
    gameState.players.map((p) => [p.id, p.name]),
  );

  // GAME OVER — winner gets their pawn-color halo on the screen
  if (gameState.phase === "GAME_OVER") {
    const sorted = Object.entries(gameState.garnets).sort(
      (a, b) => b[1] - a[1],
    );
    const winnerId = sorted[0]?.[0];
    const winnerIdx = gameState.players.findIndex((p) => p.id === winnerId);
    const winnerColor =
      winnerIdx >= 0
        ? getHorrorPlayerToken(winnerIdx)
        : "var(--cn-horror-finished-flash)";
    return (
      <div data-testid="horror-screen">
        {isObserver && <ObserverModeBanner />}
        <GameOverScreen
          garnets={gameState.garnets}
          playerNames={playerNames}
          winnerColor={winnerColor}
        />
      </div>
    );
  }

  // RACE SCORING — T18 step-by-step recap held until every player confirms.
  // D3/PR-4 — shared top frame (railMode="ready", readyFlags=hasConfirmedRecap,
  // roundGhost + poolGhost per matrix S5 row); RaceRecapView returns body+footer
  // (3-stage tab bar lives inside the footer above the confirm button).
  if (gameState.phase === "RACE_SCORING") {
    const lastResult =
      gameState.raceResults.length > 0
        ? gameState.raceResults[gameState.raceResults.length - 1]
        : undefined;
    const recapReady: Record<string, boolean> = {};
    for (const player of gameState.players) {
      recapReady[player.id] = player.hasConfirmedRecap;
    }
    return (
      <div
        data-testid="horror-screen"
        className="flex h-[100dvh] flex-col overflow-hidden"
        style={{ backgroundColor: "var(--cn-horror-bg)" }}
      >
        <HorrorTopFrame
          connectionStatus={connectionStatus}
          onLeave={handleLeaveGame}
          leaveLabel={isObserver ? "관전 나가기" : "나가기"}
          phase={gameState.phase}
          raceNumber={gameState.raceNumber}
          totalRaces={gameState.config.totalRaces}
          round={gameState.round}
          poolSize={gameState.coinPool.length}
          roundGhost
          poolGhost
          onOpenMemory={openMemory}
          memoryEnabled={memoryEnabled}
          players={gameState.players}
          turnOrder={gameState.turnOrder}
          currentTurnIndex={gameState.currentTurnIndex}
          drawPass={gameState.drawPass}
          forfeitPlayerIndex={gameState.forfeitPlayerIndex}
          fewPlayer={isFewPlayerVariant(gameState.config.playerCount)}
          garnets={gameState.garnets}
          myPlayerId={myParticipantId}
          showSecretFog={isObserver}
          railMode="ready"
          readyFlags={recapReady}
          errorMessage={lastError}
          onDismissError={() => actions.clearError()}
        />
        {lastResult ? (
          <RaceRecapView
            result={lastResult}
            players={gameState.players}
            garnets={gameState.garnets}
            playerNames={playerNames}
            trackLength={gameState.config.trackLength}
            myParticipantId={myParticipantId}
            readOnly={isObserver}
            onConfirm={() => actions.submitConfirmRecap()}
          />
        ) : (
          <div
            className="flex flex-1 items-center justify-center p-8 text-center"
            style={{ color: "var(--cn-horror-text-muted)" }}
          >
            레이스 결산 중...
          </div>
        )}
        {isObserver && <ObserverModeBanner />}
        <CoinMemorySheet
          open={memoryOpen}
          onClose={closeMemory}
          summaries={memorySummaries}
          exactSummary={memoryExactSummary}
          playerCount={gameState.players.length}
          raceNumber={gameState.raceNumber}
          totalRaces={gameState.config.totalRaces}
        />
      </div>
    );
  }

  // ROUND RECAP — T26 step-by-step round-end recap held until every player
  // confirms. Finishing rounds play only the processed zones, then advance to
  // RACE_SCORING instead of the next round.
  // D3/PR-3 — shared top frame (railMode="ready", readyFlags=hasConfirmedRoundRecap,
  // poolGhost=true per matrix); RoundRecapView returns body+footer only.
  if (gameState.phase === "ROUND_RECAP") {
    const resolution = gameState.roundResolution;
    const recapReady: Record<string, boolean> = {};
    for (const player of gameState.players) {
      recapReady[player.id] = player.hasConfirmedRoundRecap;
    }
    return (
      <div
        data-testid="horror-screen"
        className="flex h-[100dvh] flex-col overflow-hidden"
        style={{ backgroundColor: "var(--cn-horror-bg)" }}
      >
        <HorrorTopFrame
          connectionStatus={connectionStatus}
          onLeave={handleLeaveGame}
          leaveLabel={isObserver ? "관전 나가기" : "나가기"}
          phase={gameState.phase}
          raceNumber={gameState.raceNumber}
          totalRaces={gameState.config.totalRaces}
          round={gameState.round}
          poolSize={gameState.coinPool.length}
          poolGhost
          onOpenMemory={openMemory}
          memoryEnabled={memoryEnabled}
          players={gameState.players}
          turnOrder={gameState.turnOrder}
          currentTurnIndex={gameState.currentTurnIndex}
          drawPass={gameState.drawPass}
          forfeitPlayerIndex={gameState.forfeitPlayerIndex}
          fewPlayer={isFewPlayerVariant(gameState.config.playerCount)}
          garnets={gameState.garnets}
          myPlayerId={myParticipantId}
          showSecretFog={isObserver}
          railMode="ready"
          readyFlags={recapReady}
          errorMessage={lastError}
          onDismissError={() => actions.clearError()}
        />
        {resolution ? (
          <RoundRecapView
            resolution={resolution}
            players={gameState.players}
            characterPositions={gameState.characterPositions}
            trackLength={gameState.config.trackLength}
            zones={gameState.zones}
            myParticipantId={myParticipantId}
            readOnly={isObserver}
            onConfirm={() => actions.submitConfirmRoundRecap()}
          />
        ) : (
          <div
            className="flex flex-1 items-center justify-center p-8 text-center"
            style={{ color: "var(--cn-horror-text-muted)" }}
          >
            라운드 정산 중...
          </div>
        )}
        {isObserver && <ObserverModeBanner />}
        <CoinMemorySheet
          open={memoryOpen}
          onClose={closeMemory}
          summaries={memorySummaries}
          exactSummary={memoryExactSummary}
          playerCount={gameState.players.length}
          raceNumber={gameState.raceNumber}
          totalRaces={gameState.config.totalRaces}
        />
      </div>
    );
  }

  // SETUP: CHARACTER SELECTION
  // D3 container unification — same `flex h-[100dvh] flex-col overflow-hidden`
  // shell as the main-game branch so the shared HorrorTopFrame yields an
  // identical top-region footprint. RoundInfo's 라운드 / 남은 코인 slots are
  // ghosted (label-only, dim) since they have no meaning in SETUP.
  // Rail in READY mode: each dot reflects whether that player has committed
  // their secret selection (lit + ✓) or is still picking (dim). 선공 caret on
  // turnOrder[0] marks the next-race start player.
  if (gameState.phase === "SETUP_CHARACTER_SELECTION") {
    const selectionReady: Record<string, boolean> = {};
    for (const player of gameState.players) {
      selectionReady[player.id] = player.secretSelection !== null;
    }
    return (
      <div
        data-testid="horror-screen"
        className="flex h-[100dvh] flex-col overflow-hidden"
        style={{ backgroundColor: "var(--cn-horror-bg)" }}
      >
        <HorrorTopFrame
          connectionStatus={connectionStatus}
          onLeave={handleLeaveGame}
          leaveLabel={isObserver ? "관전 나가기" : "나가기"}
          phase={gameState.phase}
          raceNumber={gameState.raceNumber}
          totalRaces={gameState.config.totalRaces}
          round={gameState.round}
          poolSize={gameState.coinPool.length}
          roundGhost
          poolGhost
          onOpenMemory={openMemory}
          memoryEnabled={memoryEnabled}
          players={gameState.players}
          turnOrder={gameState.turnOrder}
          currentTurnIndex={gameState.currentTurnIndex}
          drawPass={gameState.drawPass}
          forfeitPlayerIndex={gameState.forfeitPlayerIndex}
          fewPlayer={isFewPlayerVariant(gameState.config.playerCount)}
          garnets={gameState.garnets}
          myPlayerId={myParticipantId}
          showSecretFog={isObserver}
          railMode="ready"
          readyFlags={selectionReady}
          errorMessage={lastError}
          onDismissError={() => actions.clearError()}
        />

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
          {isObserver ? (
            <div
              className="p-8 text-center"
              style={{ color: "var(--cn-horror-text-muted)" }}
            >
              플레이어들의 비밀 선택을 기다리는 중... (
              {gameState.selectionsComplete}/{gameState.config.playerCount})
            </div>
          ) : myPlayer?.secretSelection === null ? (
            <SecretSelection
              onSelect={(chars) => actions.submitSelectCharacters(chars)}
              disabled={false}
            />
          ) : (
            <div
              className="p-8 text-center"
              style={{ color: "var(--cn-horror-text-muted)" }}
            >
              다른 플레이어의 선택을 기다리는 중... (
              {gameState.selectionsComplete}/{gameState.config.playerCount})
            </div>
          )}
        </div>
        {isObserver && <ObserverModeBanner />}
        <CoinMemorySheet
          open={memoryOpen}
          onClose={closeMemory}
          summaries={memorySummaries}
          exactSummary={memoryExactSummary}
          playerCount={gameState.players.length}
          raceNumber={gameState.raceNumber}
          totalRaces={gameState.config.totalRaces}
        />
      </div>
    );
  }

  // MAIN GAME (ROUND_DRAW_AND_PLACE | ROUND_RESOLVE)
  const isRoundResolve = gameState.phase === "ROUND_RESOLVE";
  // T23-b — the active player is placing: ZoneBoard shows a drawer-only preview
  // and (when a coin is picked up) becomes the drop target.
  const isPlacing =
    isMyTurn &&
    gameState.phase === "ROUND_DRAW_AND_PLACE" &&
    gameState.drawnCoins.length > 0;

  return (
    <div
      data-testid="horror-screen"
      className="flex h-[100dvh] flex-col overflow-hidden"
      style={{ backgroundColor: "var(--cn-horror-bg)" }}
    >
      {/* D3 shared top frame — titlebar + RoundInfo (4-slot live) + TopBanner.
          Error renders as absolute overlay so the rail Y stays fixed. T6+T16+T17:
          turn-order roster (≤5p doubled), forfeit X, round-change pulse;
          expandable → player-detail popup (inside TopBanner). */}
      <HorrorTopFrame
        connectionStatus={connectionStatus}
        onLeave={handleLeaveGame}
        leaveLabel={isObserver ? "관전 나가기" : "나가기"}
        phase={gameState.phase}
        raceNumber={gameState.raceNumber}
        totalRaces={gameState.config.totalRaces}
        round={gameState.round}
        poolSize={gameState.coinPool.length}
        onOpenMemory={openMemory}
        memoryEnabled={memoryEnabled}
        players={gameState.players}
        turnOrder={gameState.turnOrder}
        currentTurnIndex={gameState.currentTurnIndex}
        drawPass={gameState.drawPass}
        forfeitPlayerIndex={gameState.forfeitPlayerIndex}
        fewPlayer={isFewPlayerVariant(gameState.config.playerCount)}
        garnets={gameState.garnets}
        myPlayerId={myParticipantId}
        showSecretFog={isObserver}
        errorMessage={lastError}
        onDismissError={() => actions.clearError()}
      />

      {/* BOARD STAGE (T24) — race track + zone board, top-anchored. The flex-1
          fills the space between the banner and the bottom bar; content sits at
          the top and the leftover is empty space. The bar is now a normal flex
          child below (no longer fixed/overlapping), so no bottom-padding
          reservation is needed. Scrolls only when the board exceeds the space. */}
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
        <RaceTrack
          positions={gameState.characterPositions}
          trackLength={gameState.config.trackLength}
        />
        <ZoneBoard
          zones={gameState.zones}
          showResolution={isRoundResolve}
          pendingByZone={isPlacing ? placementDraft.pendingByZone : undefined}
          placementActive={isPlacing && placementDraft.activeCoinIndex !== null}
          activeCoinZone={isPlacing ? placementDraft.activeCoinZone : null}
          onZoneSelect={
            isPlacing ? placementDraft.placeActiveInZone : undefined
          }
        />
      </div>

      {/* BOTTOM ACTION SURFACE (T24) — ONE bar anchored at the screen bottom as
          the last flex child (shrink-0), no longer position:fixed. The 내 선택 strip
          + status are persistent; ActionPanel slides the dynamic coin/button block
          in/out. Persists through ROUND_RESOLVE (strip + status, no controls). */}
      {!isObserver &&
        (gameState.phase === "ROUND_DRAW_AND_PLACE" || isRoundResolve) && (
          <div
            data-testid="horror-bottom-bar"
            className="shrink-0"
            style={{
              borderTop: "1px solid var(--cn-horror-surface-border)",
              backgroundColor: "var(--cn-horror-surface-secondary)",
            }}
          >
            {myPlayer?.secretSelection && (
              <SecretStrip
                secretSelection={myPlayer.secretSelection}
                open={secretOpen}
                onToggle={toggleSecret}
              />
            )}
            {gameState.phase === "ROUND_DRAW_AND_PLACE" ? (
              <ActionPanel
                isMyTurn={isMyTurn}
                currentPlayerName={currentPlayer?.name ?? ""}
                drawnCoins={gameState.drawnCoins}
                drawPending={drawRequestSig === drawTurnSig}
                disabled={!isMyTurn}
                canForfeit={isForfeitLegal}
                onRequestDraw={() => actions.submitRequestDraw()}
                placement={{
                  assignments: placementDraft.assignments,
                  activeCoinIndex: placementDraft.activeCoinIndex,
                  allAssigned: placementDraft.allAssigned,
                  anyPlaced: placementDraft.anyPlaced,
                  onCoinClick: placementDraft.pickCoin,
                  onCancel: placementDraft.cancelActive,
                  onReset: placementDraft.reset,
                  onConfirm: () => {
                    // The validator requires exactly 3 placements. allAssigned is
                    // true once every DRAWN coin has a zone; in normal play the
                    // host always draws 3 (pool exhaustion ends the round before a
                    // partial draw). Guard the length so the <3 edge can never
                    // submit a malformed payload.
                    const placements = placementDraft.buildPlacements();
                    if (placements && placements.length === 3) {
                      actions.submitDrawAndPlace(
                        placements as readonly [
                          Placement,
                          Placement,
                          Placement,
                        ],
                      );
                    }
                  },
                }}
                onForfeit={() => actions.submitForfeit()}
              />
            ) : (
              <div
                data-testid="horror-resolve-status"
                className="px-4 py-3 text-center font-semibold text-sm"
                style={{ color: "var(--cn-horror-text-muted)" }}
              >
                구역 정산 중…
              </div>
            )}
          </div>
        )}
      {isObserver &&
        (gameState.phase === "ROUND_DRAW_AND_PLACE" || isRoundResolve) && (
          <ObserverStatusDock
            phase={gameState.phase}
            currentPlayerName={currentPlayer?.name ?? ""}
            drawnCoins={gameState.drawnCoins}
          />
        )}
      {isObserver && <ObserverModeBanner />}
      <CoinMemorySheet
        open={memoryOpen}
        onClose={closeMemory}
        summaries={memorySummaries}
        exactSummary={memoryExactSummary}
        playerCount={gameState.players.length}
        raceNumber={gameState.raceNumber}
        totalRaces={gameState.config.totalRaces}
      />
    </div>
  );
}
